/**
 * The titles, the credits and the roster, read back out of a MusicXML file.
 *
 * `<part-list>` is a flat sequence with `start` and `stop` markers rather than a tree, so
 * a group is only known to be finished when its `stop` arrives. The reading below keeps
 * each open group's first part index and closes it against the last part seen, which is
 * the same span the exporter wrote and the only one the format can express.
 */

import type { Instrument, ScoreMeta, StaffGroup } from "../document/types";
import { DEFAULT_LAYOUT, TENTHS_PER_STAFF, millimetresOf } from "../document/layout";
import type { BarNumbers, Layout } from "../document/layout";
import { AT_PITCH } from "../document/pitch";
import { createId } from "../document/ids";
import type { PartId } from "../document/ids";
import { attr, child, childrenNamed, numberOf, textOf } from "../xml/read";
import { drumsOf } from "./import-drums";
import type { DrumListing } from "./import-drums";
import type { XmlElement } from "../xml/parse";

/**
 * One instrument a part's roster declares, by the id the file names it.
 *
 * A part lists more than one when its player picks another up partway through, and the bar
 * where they do points at the id rather than repeating the name. So the whole list is kept,
 * not only the first entry: the bars are read against it.
 */
export interface RosterInstrument {
  readonly name: string;
  readonly sound: string | null;
  readonly midiProgram: number | null;
}

/** One `<score-part>`, before it has any music. */
export interface PartEntry {
  /** The file's own id for the part, used to match it to its `<part>` element. */
  readonly xmlId: string;
  readonly name: string;
  readonly abbreviation: string;
  readonly instrument: Instrument;
  /** Everything the part is played on, by roster id, for the bars that take one up. */
  readonly roster: ReadonlyMap<string, RosterInstrument>;
  /** The drums the part lists, empty unless it is a percussion part. */
  readonly drums: DrumListing;
}

/** A group, as the indexes of the parts it spans. */
export interface GroupEntry {
  readonly symbol: StaffGroup["symbol"];
  readonly barlineSpan: boolean;
  readonly name: string | null;
  readonly from: number;
  readonly to: number;
}

/**
 * The page a file was engraved on, as far as the document can hold it.
 *
 * A file states its geometry in tenths and states, in `<scaling>`, what a tenth is worth
 * in millimetres; the two together are the only way to know how large anything actually
 * is. Everything is converted at the door so that the document is in millimetres
 * throughout and no later reader has to remember which unit it is holding.
 *
 * Anything the file does not say keeps the default. A file with no `<defaults>` at all --
 * and there are many -- is a score somebody engraved on a page they did not record, so
 * reading A4 into it is not a claim about that page, it is the page we will print it on.
 *
 * Whether silent bars are gathered is not in `<defaults>` and cannot be: MusicXML writes
 * the gathering itself, bar by bar, and not the decision to gather. So it is read off the
 * bars -- see `import-bar.ts` -- and handed in here by the caller.
 *
 * Leaving empty staves out of a system is the same kind of thing and has no `<defaults>`
 * setting either, so an imported score is read as drawing all of its staves. That is the
 * safe way round for an editor: every part can be clicked, and a user who wants the
 * condensed page asks for it.
 *
 * How the bars are numbered is not in `<defaults>` either -- it is a `<print>` instruction
 * standing in a bar -- so it is read off the music by {@link statedBarNumbers}.
 */
export function readLayout(root: XmlElement, multiRests: boolean): Layout {
  const barNumbers = statedBarNumbers(root);
  const defaults = child(root, "defaults");
  if (defaults === null) return { ...DEFAULT_LAYOUT, multiRests, barNumbers };

  const scaling = child(defaults, "scaling");
  const millimetres = numberOf(scaling, "millimeters", 0);
  const unit = numberOf(scaling, "tenths", TENTHS_PER_STAFF);
  const staffHeight =
    millimetres > 0 && unit > 0
      ? (millimetres * TENTHS_PER_STAFF) / unit
      : DEFAULT_LAYOUT.staffHeight;

  const page = child(defaults, "page-layout");
  const mm = (element: XmlElement | null, name: string, fallback: number): number => {
    const stated = numberOf(element, name, Number.NaN);
    return Number.isFinite(stated) ? millimetresOf(stated, staffHeight) : fallback;
  };

  // `<page-margins>` may be given once for both sides of the paper or once per side; a
  // score is printed on one page at a time here, so the pair is read as the left-hand one.
  const margins = childrenNamed(page, "page-margins");
  const both =
    margins.find((element) => (attr(element, "type") ?? "both") === "both") ?? margins[0] ?? null;

  return {
    staffHeight,
    pageWidth: mm(page, "page-width", DEFAULT_LAYOUT.pageWidth),
    pageHeight: mm(page, "page-height", DEFAULT_LAYOUT.pageHeight),
    margins: {
      left: mm(both, "left-margin", DEFAULT_LAYOUT.margins.left),
      right: mm(both, "right-margin", DEFAULT_LAYOUT.margins.right),
      top: mm(both, "top-margin", DEFAULT_LAYOUT.margins.top),
      bottom: mm(both, "bottom-margin", DEFAULT_LAYOUT.margins.bottom),
    },
    systemGap: mm(child(defaults, "system-layout"), "system-distance", DEFAULT_LAYOUT.systemGap),
    staffGap: mm(child(defaults, "staff-layout"), "staff-distance", DEFAULT_LAYOUT.staffGap),
    multiRests,
    hideEmptyStaves: DEFAULT_LAYOUT.hideEmptyStaves,
    barNumbers,
  };
}

/**
 * How the file says its bars are numbered, which is a `<print>` instruction rather than a
 * default.
 *
 * The first statement in the first part is the answer. It is where every writer puts it,
 * it is the only place this package writes it, and the document has one setting for the
 * whole score, so a file that changed its mind halfway through would have to be read as
 * one thing or the other in any case. A file that says nothing is numbered at the start of
 * each system, which is what a reader left to itself draws.
 *
 * `measure` comes back as every bar rather than as a count, the format having no count to
 * state; see `export-print.ts` for the other end of that.
 */
function statedBarNumbers(root: XmlElement): BarNumbers {
  const part = childrenNamed(root, "part")[0];
  if (part === undefined) return DEFAULT_LAYOUT.barNumbers;
  for (const measure of childrenNamed(part, "measure")) {
    for (const print of childrenNamed(measure, "print")) {
      const stated = child(print, "measure-numbering")?.text.trim();
      if (stated === "none" || stated === "system") return stated;
      if (stated === "measure") return 1;
    }
  }
  return DEFAULT_LAYOUT.barNumbers;
}

function creator(root: XmlElement, type: string): string | null {
  const identification = child(root, "identification");
  const match = childrenNamed(identification, "creator").find(
    (element) => attr(element, "type") === type,
  );
  return match?.text.trim() || null;
}

/** Title, composer and the rest of the header. */
export function readMeta(root: XmlElement): ScoreMeta {
  const credit = childrenNamed(root, "credit")
    .map((element) => textOf(element, "credit-words"))
    .find((text) => text !== null && text.trim() !== "");
  const title = textOf(child(root, "work"), "work-title") ?? textOf(root, "movement-title") ?? "";

  return {
    title: title.trim() || "Untitled",
    subtitle: credit?.trim() || null,
    composer: creator(root, "composer"),
    arranger: creator(root, "arranger"),
    lyricist: creator(root, "lyricist") ?? creator(root, "poet"),
    copyright: textOf(child(root, "identification"), "rights")?.trim() || null,
    movementNumber: textOf(root, "movement-number")?.trim() || null,
    movementTitle: textOf(root, "movement-title")?.trim() || null,
  };
}

function instrumentOf(element: XmlElement, name: string): Instrument {
  const score = child(element, "score-instrument");
  const midi = child(element, "midi-instrument");
  const program = midi === null ? null : numberOf(midi, "midi-program", 0);
  return {
    name: textOf(score, "instrument-name")?.trim() || name,
    sound: textOf(score, "instrument-sound")?.trim() || null,
    // MusicXML numbers General MIDI programs from one; the document numbers them from zero.
    midiProgram: program !== null && program > 0 ? program - 1 : null,
    transposition: AT_PITCH,
    // A tuning is on the staff rather than in the roster, so it is put on the part in
    // `import.ts` once the attributes of its first bar have been read.
    tuning: null,
    // A kit cannot be built from the roster alone: half of it is on the notes. It is put
    // together in `import.ts`, once the part has been read.
    percussion: null,
    // How the part's signature is arrived at is read from the signature it prints, which
    // is in its first bar rather than in the roster: `import.ts` again.
    keyPrinting: "derived",
  };
}

/**
 * Every `<score-instrument>` the part declares, with the MIDI program that belongs to it.
 *
 * The two halves are separate elements paired by id -- the format wants all of the
 * `<score-instrument>` elements before any `<midi-instrument>` -- so the programs are
 * gathered first and the roster is built against them.
 */
function rosterOf(element: XmlElement): ReadonlyMap<string, RosterInstrument> {
  const programs = new Map<string, number>();
  for (const midi of childrenNamed(element, "midi-instrument")) {
    const id = attr(midi, "id");
    const program = numberOf(midi, "midi-program", 0);
    // MusicXML numbers General MIDI programs from one; the document numbers them from zero.
    if (id !== null && program > 0) programs.set(id, program - 1);
  }

  const roster = new Map<string, RosterInstrument>();
  for (const entry of childrenNamed(element, "score-instrument")) {
    const id = attr(entry, "id");
    if (id === null) continue;
    roster.set(id, {
      name: textOf(entry, "instrument-name")?.trim() ?? "",
      sound: textOf(entry, "instrument-sound")?.trim() || null,
      midiProgram: programs.get(id) ?? null,
    });
  }
  return roster;
}

const SYMBOLS: readonly StaffGroup["symbol"][] = ["bracket", "brace", "line", "none"];

/** The roster and the brackets around it. */
export function readPartList(root: XmlElement): {
  readonly parts: readonly PartEntry[];
  readonly groups: readonly GroupEntry[];
} {
  const list = child(root, "part-list");
  const parts: PartEntry[] = [];
  const groups: GroupEntry[] = [];
  const open = new Map<string, Omit<GroupEntry, "to">>();

  for (const element of list?.children ?? []) {
    if (element.name === "score-part") {
      const name = textOf(element, "part-name")?.trim() ?? "";
      parts.push({
        xmlId: attr(element, "id") ?? `P${parts.length + 1}`,
        name: name || `Part ${parts.length + 1}`,
        abbreviation: textOf(element, "part-abbreviation")?.trim() ?? "",
        instrument: instrumentOf(element, name || `Part ${parts.length + 1}`),
        roster: rosterOf(element),
        drums: drumsOf(element),
      });
      continue;
    }
    if (element.name !== "part-group") continue;

    const number = attr(element, "number") ?? "1";
    if (attr(element, "type") === "stop") {
      const started = open.get(number);
      if (started !== undefined) {
        groups.push({ ...started, to: Math.max(started.from, parts.length - 1) });
        open.delete(number);
      }
      continue;
    }
    const symbol = textOf(element, "group-symbol")?.trim() ?? "bracket";
    open.set(number, {
      symbol: SYMBOLS.includes(symbol as StaffGroup["symbol"])
        ? (symbol as StaffGroup["symbol"])
        : "bracket",
      barlineSpan: (textOf(element, "group-barline") ?? "yes") !== "no",
      name: textOf(element, "group-name")?.trim() || null,
      from: parts.length,
    });
  }

  // A group the file never closes still spans from where it opened to the last part.
  for (const started of open.values()) {
    groups.push({ ...started, to: Math.max(started.from, parts.length - 1) });
  }

  return { parts, groups };
}

/** A group over the part ids it turned out to span. */
export function groupOf(entry: GroupEntry, ids: readonly PartId[]): StaffGroup {
  return {
    id: createId("group"),
    symbol: entry.symbol,
    barlineSpan: entry.barlineSpan,
    name: entry.name,
    parts: ids.slice(entry.from, entry.to + 1),
  };
}
